import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@deskbay/shared/services/supabase/client';
import { supabaseDb } from '@deskbay/shared/services/supabase/database';
import { RestaurantData, Booking } from '@deskbay/shared/types';
import type { Session } from '@supabase/supabase-js';
import { logger } from '@deskbay/shared';
import { storage } from '../services/storage';
import {
    getLastRestaurantId,
    setLastRestaurantId,
    consumeLegacyLastRestaurantId,
} from '../services/storage/lastRestaurant';
import { resetAdminRoleCache } from '../hooks/useAdminRole';
import { setSentryUser, clearSentryUser } from '../services/sentry';
import { useRestaurantStore } from '../store/restaurantStore';
import { useAppFocus } from '../hooks/useAppFocus';
import { useAppState } from '../hooks/useAppState';
import { oneSignalService } from '../services/oneSignalService';
import { getDeviceInfo } from '../utils/deviceInfo';


interface AuthContextType {
    session: Session | null;
    restaurantData: RestaurantData | null;
    availableRestaurants: AvailableRestaurant[]; // Lista ristoranti accessibili
    selectedRestaurantId: string | null; // ID Ristorante Attivo
    switchRestaurant: (restaurantId: string) => Promise<void>;
    isLoading: boolean;
    hasInitialized: boolean; // True after first auth check completes
    /**
     * True quando il listener Supabase ha effettivamente comunicato lo stato della
     * sessione. Finché è false, `session === null` significa "non ancora noto" e non
     * "non loggato": le guardie di routing non devono dedurne un logout.
     */
    authResolved: boolean;
    loadAppData: (userId: string, sessionUser?: Session['user'], preferredRestaurantId?: string) => Promise<void>;
    handleDataChange: (data: RestaurantData, mode?: 'full' | 'directory-only' | 'local-only') => Promise<boolean>;
    /**
     * Allinea la lista prenotazioni in memoria (React state + Zustand store) senza
     * toccare il DB. Da usare dopo che `bookingService` ha già persistito la modifica
     * sulla tabella `bookings`: passare da `handleDataChange` riscriverebbe l'intera
     * riga `restaurants` (~50 colonne) inutilmente, con rischio di lost update.
     *
     * L'updater riceve la lista corrente letta dallo store, non una catturata in
     * closure: due mutazioni ravvicinate (es. due conferme rapide) non si sovrascrivono.
     */
    applyLocalBookings: (updater: (prev: Booking[]) => Booking[]) => void;
    handleLogout: (skipRedirect?: boolean) => Promise<void>;
    hasPermission: (permission: string) => boolean;
    error: Error | null;
    // [ADMIN] Cached admin status - elimina chiamate RPC ripetute
    isAdmin: boolean;
    isAdminImpersonating: boolean;
    isAdminLoading: boolean;
    isPageReady: boolean;
    setPageReady: (ready: boolean) => void;
    refreshCurrentRestaurant: () => Promise<void>;
}

interface AvailableRestaurant {
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
    owner_id: string;
    onboarding_status: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ─── BOOKING REALTIME UTILITIES ──────────────────────────────────────────────

/** Rappresentazione grezza di una riga della tabella `bookings` (snake_case DB). */
export type BookingRow = {
    id: string;
    booking_number?: string;
    booking_date: string;
    booking_time: string;
    party_size: number;
    customer_name: string;
    customer_phone: string;
    customer_email: string;
    notes?: string;
    created_at: string;
    table_id?: string;
    status: string;
    cancellation_token?: string;
    lang?: string;
    is_arrived?: boolean;
    cancelled_by?: string;
    rejection_reason?: string | null;
    notification_preference?: string;
    restaurant_id?: string;
    source?: string;
    [key: string]: unknown;
};

/** Mappa una riga DB (snake_case) nel tipo Booking usato dall'UI (camelCase). */
export function mapDbRowToBooking(raw: BookingRow): Booking {
    return {
        id: raw.id,
        booking_number: raw.booking_number,
        booking_date: raw.booking_date,
        booking_time: raw.booking_time,
        partySize: raw.party_size,
        name: raw.customer_name,
        phone: raw.customer_phone,
        email: raw.customer_email,
        notes: raw.notes,
        createdAt: raw.created_at,
        tableId: raw.table_id,
        status: raw.status as Booking['status'],
        lang: raw.lang,
        isArrived: raw.is_arrived,
        cancelled_by: raw.cancelled_by as Booking['cancelled_by'],
        rejectionReason: raw.rejection_reason,
        notification_preference: raw.notification_preference as Booking['notification_preference'],
        restaurantId: raw.restaurant_id,
        source: raw.source as Booking['source'],
    };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [session, setSession] = useState<Session | null>(null);
    const [restaurantData, setRestaurantData] = useState<RestaurantData | null>(null);
    const [availableRestaurants, setAvailableRestaurants] = useState<AvailableRestaurant[]>([]); // [NEW]
    const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | null>(null); // [NEW]
    const [isLoading, setIsLoading] = useState(true);
    const [hasInitialized, setHasInitialized] = useState(false); // True after first auth check
    const [error, setError] = useState<Error | null>(null);
    const isLoadingRef = useRef(true);
    useEffect(() => { isLoadingRef.current = isLoading; }, [isLoading]);
    const navigate = useNavigate();
    const location = useLocation();

    // [ADMIN] Cached admin status - check once per session
    const [isAdmin, setIsAdmin] = useState(false);
    const [isAdminImpersonating, setIsAdminImpersonating] = useState(false);
    const [isAdminLoading, setIsAdminLoading] = useState(false);
    const [isPageReady, setPageReady] = useState(false);

    // True appena il listener Supabase si è pronunciato sulla sessione (INITIAL_SESSION,
    // SIGNED_IN o SIGNED_OUT), a prescindere dal caricamento dati.
    // Distinto da `hasInitialized`, che il safety timer può forzare a true PRIMA che la
    // sessione sia nota: in quel caso `session === null` non significa "non loggato" ma
    // "non lo sappiamo ancora", e ProtectedRoute non deve dedurne un logout.
    const [authResolved, setAuthResolved] = useState(false);

    // Refs per evitare stale closures
    const sessionRef = useRef<Session | null>(null);
    const restaurantDataRef = useRef<RestaurantData | null>(null);

    // ─── EPOCH GUARD (anti race su cambio account) ───────────────────────────
    // Incrementato ad ogni cambio di identità (login, logout, switch account).
    // `loadAppData` / `loadRestaurantDetail` catturano il valore all'ingresso e,
    // dopo ogni await, scrivono nello stato solo se l'epoch è ancora quello:
    // un caricamento partito per l'utente precedente (0,5-5s su nativo) non può
    // più sovrascrivere i dati di quello nuovo.
    const authEpochRef = useRef(0);

    // Utente per cui i dati in memoria sono stati caricati (id di auth, non il
    // profilo: sotto impersonificazione restano diversi). Serve al dedup degli
    // eventi auth per non "congelare" uno stato incoerente.
    const loadedForUserIdRef = useRef<string | null>(null);

    // Utente EFFETTIVO del caricamento corrente (target dell'impersonificazione
    // se attiva, altrimenti l'utente di sessione). Usato per leggere/scrivere la
    // preferenza "ultimo ristorante" sulla stessa chiave in lettura e scrittura.
    const effectiveUserIdRef = useRef<string | null>(null);

    useEffect(() => { sessionRef.current = session; }, [session]);
    useEffect(() => { restaurantDataRef.current = restaurantData; }, [restaurantData]);

    // CRITICAL FIX: Track app foreground/background state
    const isAppActive = useAppState();

    // Admin status check removed - will be handled by admin app


    // Load app data (Resolving logic)
    const loadAppData = async (userId: string, sessionUser?: Session['user'], preferredRestaurantId?: string) => {
        const startTime = performance.now();

        // Vedi authEpochRef: fotografiamo l'identità all'ingresso e la ricontrolliamo
        // dopo ogni await prima di toccare lo stato.
        const epoch = authEpochRef.current;
        const isCurrent = () => epoch === authEpochRef.current;

        setIsLoading(true);
        setError(null);

        // [ADMIN IMPERSONATION] Check if admin is impersonating a user
        let impersonatingNow = false;
        try {
            const impersonationData = sessionStorage.getItem('deskbay_impersonation');
            if (impersonationData) {
                const { targetUserId, isActive } = JSON.parse(impersonationData) as { targetUserId?: string; isActive?: boolean };
                if (isActive && targetUserId) {
                    logger.auth.info(`Admin impersonation active, loading data for target user: ${targetUserId}`, 'loadAppData');
                    userId = targetUserId;
                    impersonatingNow = true;
                    setIsAdmin(true);
                    setIsAdminImpersonating(true);
                }
            }
        } catch (e: unknown) {
            logger.auth.warn('Failed to parse impersonation state', 'loadAppData');
        }

        // Da qui in poi `userId` è l'utente effettivo (target se impersonificazione attiva):
        // è quello a cui vanno intestate lettura e scrittura della preferenza ristorante.
        effectiveUserIdRef.current = userId;

        // [OPTIMIZATION] Optimistic loading
        const lastId = await getLastRestaurantId(userId);

        // Chiave globale pre-migrazione: usabile SOLO nella risoluzione più sotto, dove
        // `restaurants.find()` verifica che il ristorante appartenga a questo utente.
        // Mai per il prefetch, che partirebbe prima di quella verifica.
        const legacyId = lastId ? null : await consumeLegacyLastRestaurantId();

        if (!isCurrent()) {
            logger.auth.info('loadAppData annullata: identità cambiata durante la lettura preferenze', 'loadAppData');
            return;
        }

        let optimisticPromise: Promise<RestaurantData | null> | null = null;

        if (lastId) {
            logger.auth.debug(`Optimistic load started for: ${lastId}`, 'loadAppData');
            // Passiamo userId per garantire permessi corretti in caso di impersonificazione
            // Passiamo sessionUser per evitare getUser() su native (AuthSessionMissingError)
            optimisticPromise = supabaseDb.getRestaurantDetail(lastId, userId, sessionUser, impersonatingNow).catch(err => {
                logger.auth.warn('Optimistic fetch failed, ignoring', 'loadAppData');
                return null;
            });
        }

        try {
            // 1. Fetch Lista Ristoranti (Request A)
            const restaurantsPromise = supabaseDb.getAvailableRestaurants(userId);

            // Wait for both (if optimistic exists) logic handled below
            const restaurants = await restaurantsPromise;

            if (!isCurrent()) {
                logger.auth.info('loadAppData annullata: identità cambiata durante il fetch ristoranti', 'loadAppData');
                return;
            }
            setAvailableRestaurants(restaurants);

            if (restaurants.length === 0) {
                // Nessun ristorante
                logger.auth.info('Nessun ristorante trovato per l\'utente', 'loadAppData');
                setRestaurantData(null);
                setSelectedRestaurantId(null);
            } else {
                // 3. Risolvi quale ristorante caricare
                let targetRestaurant = restaurants[0];
                const preferredRestaurant = preferredRestaurantId ? restaurants.find(r => r.id === preferredRestaurantId) : null;
                // `legacyId` entra in gioco solo qui: `find()` garantisce che il ristorante
                // sia fra quelli accessibili a QUESTO utente, quindi un valore ereditato da
                // un altro account viene semplicemente ignorato.
                const lastUsedRestaurant = restaurants.find(r => r.id === lastId)
                    ?? (legacyId ? restaurants.find(r => r.id === legacyId) : undefined);

                // Logic resolution
                if (preferredRestaurant) {
                    // Caller explicitly requested a specific restaurant (e.g. post-onboarding of a new restaurant)
                    targetRestaurant = preferredRestaurant;
                } else if (lastUsedRestaurant) {
                    targetRestaurant = lastUsedRestaurant;
                } else if (!impersonatingNow) {
                    // For regular users, prefer the first completed restaurant
                    // Admin impersonation must see the actual first restaurant regardless of status
                    const firstComplete = restaurants.find(r => r.onboarding_status === 'completed');
                    if (firstComplete) targetRestaurant = firstComplete;
                }

                // [OPTIMIZATION] Check if optimistic load matches our target
                let loadedData: RestaurantData | null = null;

                if (optimisticPromise && targetRestaurant.id === lastId) {
                    logger.auth.debug('Waiting for optimistic result...', 'loadAppData');
                    loadedData = await optimisticPromise;

                    if (!isCurrent()) {
                        logger.auth.info('loadAppData annullata: identità cambiata durante il prefetch', 'loadAppData');
                        return;
                    }

                    if (loadedData) {
                        logger.auth.debug('Optimistic hit! Using preloaded data.', 'loadAppData');
                    } else {
                        logger.auth.debug('Optimistic miss (null or error).', 'loadAppData');
                    }
                }

                // If optimistic failed or wasn't applicable, load normally
                if (loadedData) {
                    setSelectedRestaurantId(targetRestaurant.id);
                    setRestaurantData(loadedData);
                    // Nessuna scrittura della preferenza qui: questo ramo richiede
                    // targetRestaurant.id === lastId, cioè la chiave contiene già
                    // esattamente questo valore (una write nativa costa ~70ms).
                    // Set Sentry user context
                    setSentryUser(userId, targetRestaurant.id);
                } else {
                    // L'epoch è ancora valido (controllato sopra): deleghiamo il resto
                    // a loadRestaurantDetail, che applica lo stesso guard sui propri await.
                    await loadRestaurantDetail(targetRestaurant.id, userId, sessionUser, impersonatingNow);
                }
            }
        } catch (e: unknown) {
            logger.auth.error('Errore caricamento dati', e, 'loadAppData', { userId, restaurantCount: availableRestaurants.length });
            // Un errore su un caricamento ormai superato non deve mostrare la schermata
            // di errore né azzerare i dati dell'utente che nel frattempo è entrato.
            if (isCurrent()) {
                setError(e instanceof Error ? e : new Error(String(e)));
                setRestaurantData(null);
            }
        } finally {
            const totalTime = performance.now() - startTime;
            logger.performance.info(`loadAppData completed in ${totalTime.toFixed(0)}ms`, 'loadAppData');

            if (totalTime > 2000) {
                logger.performance.warn(`SLOW loadAppData: ${totalTime.toFixed(0)}ms (target: <2000ms)`, 'loadAppData');
            }

            // Sbloccare l'UI da un caricamento superato spegnerebbe lo spinner mentre
            // quello nuovo è ancora in corso: ProtectedRoute vedrebbe restaurantData
            // null con hasInitialized true e manderebbe l'utente su /onboarding.
            // Ci pensa il caricamento corrente (o, come rete di sicurezza, il timer).
            if (isCurrent()) {
                setIsLoading(false);
                setHasInitialized(true);
                setPageReady(true);
            }
        }
    };

    // Helper per caricare e impostare il ristorante attivo
    const loadRestaurantDetail = async (restaurantId: string, overrideUserId?: string, sessionUser?: Session['user'], adminBypass?: boolean) => {
        const startTime = performance.now();
        const epoch = authEpochRef.current;
        const isCurrent = () => epoch === authEpochRef.current;

        setSelectedRestaurantId(restaurantId);

        // Save preference — intestata all'utente EFFETTIVO, cioè lo stesso a cui è
        // intestata la lettura in loadAppData. Il ref viene prima di `overrideUserId`
        // perché refreshCurrentRestaurant passa l'utente di sessione: sotto
        // impersonificazione è l'admin, e scriverebbe la preferenza su una chiave che
        // nessuno rilegge. Nel percorso normale i due valori coincidono.
        const prefUserId = effectiveUserIdRef.current ?? overrideUserId ?? sessionRef.current?.user?.id ?? null;
        if (prefUserId) {
            await setLastRestaurantId(prefUserId, restaurantId);
        }

        try {
            // Passiamo l'id utente (reale o simulato) per calcolare correttamente i permessi
            // Passiamo sessionUser per evitare getUser() su native (AuthSessionMissingError)
            const data = await supabaseDb.getRestaurantDetail(restaurantId, overrideUserId, sessionUser, adminBypass);
            const duration = performance.now() - startTime;
            logger.performance.info(`loadRestaurantDetail completed in ${duration.toFixed(0)}ms`, 'loadRestaurantDetail');

            // Scarta il risultato se nel frattempo l'utente è cambiato: senza questo
            // controllo il dettaglio del ristorante precedente sovrascriveva quello nuovo.
            if (!isCurrent()) {
                logger.auth.info('Dettaglio ristorante scartato: identità cambiata durante il caricamento', 'loadRestaurantDetail');
                return;
            }

            setRestaurantData(data);
            // Set Sentry user context
            if (overrideUserId) {
                setSentryUser(overrideUserId, restaurantId);
            }


        } catch (err: unknown) {
            logger.auth.error('Failed to load restaurant detail', err, 'loadRestaurantDetail', { restaurantId });
            throw err;
        }
    };

    // [NEW] Funzione esposta per cambiare ristorante
    const switchRestaurant = async (restaurantId: string) => {
        if (restaurantId === selectedRestaurantId) return;
        // Security guard: only switch to a restaurant the user has access to.
        // This prevents cross-account context corruption via deeplinks or any
        // other caller passing an arbitrary restaurantId.
        const isAccessible = availableRestaurants.some(r => r.id === restaurantId);
        if (!isAccessible) {
            const err = new Error(`switchRestaurant: restaurant ${restaurantId} is not accessible to current user`);
            logger.auth.warn('switchRestaurant blocked: restaurant not in availableRestaurants', 'switchRestaurant', {
                restaurantId,
                availableIds: availableRestaurants.map(r => r.id)
            });
            throw err;
        }
        setIsLoading(true);
        try {
            await loadRestaurantDetail(restaurantId);
            // Force navigation to dashboard on switch
            navigate('/dashboard');
        } catch (e: unknown) {
            logger.auth.error('switchRestaurant failed', e, 'switchRestaurant', { fromRestaurantId: selectedRestaurantId, toRestaurantId: restaurantId });
            setError(e instanceof Error ? e : new Error(String(e)));
        } finally {
            setIsLoading(false);
        }
    };

    // ─── GLOBAL BOOKING REALTIME SUBSCRIPTION ────────────────────────────────
    // Vive in AuthContext (sempre attiva per tutta la sessione) invece che in
    // BookingManager (che si smonta navigando su altre pagine).
    // Aggiorna lo Zustand store direttamente tramite API imperativa per evitare stale closure.
    // Dipende SOLO da directoryId — NON da data.bookings per evitare il bug di
    // re-subscribe ad ogni modifica (canale ricreato inutilmente ad ogni cambio).
    useEffect(() => {
        const restaurantId = restaurantData?.directoryId;
        if (!restaurantId) return;

        logger.auth.info(`Booking realtime: subscribing for restaurant ${restaurantId}`, 'AuthContext');

        const channel = supabase
            .channel(`bookings-global-${restaurantId}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'bookings',
                    filter: `restaurant_id=eq.${restaurantId}`,
                },
                (payload) => {
                    logger.auth.debug('Booking realtime event received', 'AuthContext', { type: payload.eventType });

                    // Lettura imperativa dallo store per evitare stale closure React
                    const store = useRestaurantStore.getState();
                    const currentBookings = store.data?.bookings ?? [];

                    if (payload.eventType === 'INSERT') {
                        const raw = payload.new as BookingRow;
                        // Deduplicazione: evita doppio-add se l'utente ha creato la prenotazione
                        // dall'interno dell'app (ManualBookingModal) che aggiorna già lo store
                        if (currentBookings.some(b => b.id === raw.id)) return;
                        const mapped = mapDbRowToBooking(raw);
                        const newList = [mapped, ...currentBookings];
                        store.updateBookings(newList);
                        
                        // CRITICAL FIX: Se l'app è in background, accoda l'evento invece di settarlo come lastBookingEvent
                        // Questo permette di riprodurlo al ritorno in foreground con suono e toast
                        const event = { type: 'INSERT' as const, booking: mapped, timestamp: Date.now() };
                        if (!isAppActive) {
                            logger.auth.info('App in background: accodando evento INSERT per riproduzione futura', 'AuthContext', { bookingId: mapped.id });
                            store.addPendingBookingEvent(event);
                        } else {
                            store.setLastBookingEvent(event);
                        }
                        
                        // Sync anche il React state di AuthContext: evita che il loop di sync
                        // in Dashboard.tsx sovrascriva lo Zustand store con la lista vecchia
                        setRestaurantData(prev => {
                            if (!prev) return prev;
                            if (prev.bookings?.some(b => b.id === raw.id)) return prev;
                            return { ...prev, bookings: newList };
                        });
                    } else if (payload.eventType === 'UPDATE') {
                        const raw = payload.new as BookingRow;
                        const mapped = mapDbRowToBooking(raw);
                        const newList = currentBookings.map(b =>
                            b.id === raw.id ? { ...b, ...mapped } : b
                        );
                        store.updateBookings(newList);
                        
                        // CRITICAL FIX: Stesso meccanismo per UPDATE
                        const event = { type: 'UPDATE' as const, booking: mapped, timestamp: Date.now() };
                        if (!isAppActive) {
                            logger.auth.info('App in background: accodando evento UPDATE per riproduzione futura', 'AuthContext', { bookingId: mapped.id });
                            store.addPendingBookingEvent(event);
                        } else {
                            store.setLastBookingEvent(event);
                        }
                        
                        setRestaurantData(prev => prev ? {
                            ...prev,
                            bookings: (prev.bookings ?? []).map(b => b.id === raw.id ? { ...b, ...mapped } : b)
                        } : prev);
                    } else if (payload.eventType === 'DELETE') {
                        const id = (payload.old as { id: string }).id;
                        const newList = currentBookings.filter(b => b.id !== id);
                        store.updateBookings(newList);
                        // Nessun evento UI per DELETE — nessun toast necessario
                        setRestaurantData(prev => prev ? {
                            ...prev,
                            bookings: (prev.bookings ?? []).filter(b => b.id !== id)
                        } : prev);
                    }
                }
            )
            .subscribe((status) => {
                if (status === 'SUBSCRIBED') {
                    logger.auth.info('Booking realtime: connected and listening', 'AuthContext');
                } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
                    logger.auth.warn(`Booking realtime channel issue: ${status} — events may not arrive`, 'AuthContext');
                } else {
                    logger.auth.debug(`Booking realtime channel status: ${status}`, 'AuthContext');
                }
            });

        return () => {
            logger.auth.info('Booking realtime: unsubscribing', 'AuthContext');
            supabase.removeChannel(channel);
        };
    }, [restaurantData?.directoryId]);

    // ─── DASHBOARD PRESENCE (Server Monitor) ─────────────────────────────────
    useEffect(() => {
        const userId = session?.user?.id;
        const restaurantId = restaurantData?.directoryId;
        if (!userId || !restaurantId) return;

        const channel = supabase.channel('monitor:dashboard', {
            config: { presence: { key: userId } }
        });

        channel.subscribe(async (status) => {
            if (status !== 'SUBSCRIBED') return;
            await channel.track({
                user_id: userId,
                restaurant_id: restaurantId,
                restaurant_name: restaurantData?.businessInfo?.name ?? 'Unknown',
                joined_at: new Date().toISOString(),
                ...getDeviceInfo(),
            });
        });

        return () => { supabase.removeChannel(channel); };
    }, [session?.user?.id, restaurantData?.directoryId]);

    // ─── DEFERRED EMAIL-CHANGE SYNC (fresh-device confirmation) ──────────────
    // Quando il link di conferma cambio email viene aperto su un dispositivo/browser
    // senza una sessione Deskbay preesistente (caso tipico: il NUOVO proprietario clicca
    // il link sul proprio telefono), il client Supabase emette SIGNED_IN invece di
    // USER_UPDATED — l'handler USER_UPDATED più sotto (che chiama record_email_change()
    // e sincronizza fw360_customers) non parte mai in quel caso. AuthCallback.tsx marca
    // questa situazione con un flag in sessionStorage; qui lo consumiamo non appena
    // restaurantData è pronto (directoryId disponibile). Puramente additivo: se il flag
    // non c'è (99% dei login normali), l'effect esce subito senza alcun effetto.
    useEffect(() => {
        const directoryId = restaurantData?.directoryId;
        const userEmail = session?.user?.email;
        if (!directoryId || !userEmail) return;

        let pending: string | null = null;
        try {
            pending = sessionStorage.getItem('deskbay_pending_email_change_sync');
        } catch (e) {
            return;
        }
        if (pending !== '1') return;

        try {
            sessionStorage.removeItem('deskbay_pending_email_change_sync');
        } catch (e) { /* best-effort */ }

        (async () => {
            try {
                await supabase.rpc('record_email_change');
                logger.auth.info('Deferred email-change sync: rate-limit timestamp recorded', 'AuthContext');
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : String(err);
                logger.auth.warn('Deferred email-change sync: record_email_change failed', 'AuthContext', { error: msg });
            }

            supabase.functions.invoke('sync-fw360-customer', {
                body: { directoryId, email: userEmail }
            }).then(({ error }) => {
                if (error) {
                    logger.auth.warn('Deferred email-change sync: fw360_customers sync failed (non-critical)', 'AuthContext', { error: error.message });
                } else {
                    logger.auth.debug('Deferred email-change sync: fw360_customers synced', 'AuthContext');
                }
            }).catch(() => { /* non-critical, silently ignore */ });
        })();
    }, [restaurantData?.directoryId, session?.user?.email]);

    // ─── FOREGROUND RECOVERY — BOOKINGS ──────────────────────────────────────
    // Al ritorno in foreground (dopo background su mobile, o cambio tab su web),
    // esegue un refetch leggero degli ultimi 7 giorni come safety net per i casi
    // in cui il WebSocket fosse caduto durante il background.
    // CRITICAL FIX: Legge restaurantId dallo Zustand store (sempre aggiornato, anche in background)
    // invece che da restaurantDataRef (che può essere stale se il componente è congelato).
    const handleBookingFocus = useCallback(async () => {
        // Nessuna sessione attiva → niente refetch. Senza questo guard, al ritorno in
        // foreground dopo un logout si continuava a interrogare il ristorante rimasto
        // nello store (bloccato dalla RLS, ma è una richiesta cross-account).
        if (!sessionRef.current) return;

        // Leggi dallo store imperativo — sempre aggiornato anche se l'app era in background
        const store = useRestaurantStore.getState();
        const restaurantId = store.data?.directoryId;
        if (!restaurantId) return;

        // Lo store può restare indietro durante un cambio account: se il context ha già
        // dati di un altro ristorante, salta questo giro (il caricamento in corso porta
        // comunque le prenotazioni aggiornate).
        const contextRestaurantId = restaurantDataRef.current?.directoryId;
        if (contextRestaurantId && contextRestaurantId !== restaurantId) {
            logger.auth.debug('Focus refetch saltato: store e context su ristoranti diversi', 'AuthContext');
            return;
        }

        logger.auth.info('App focus: refetching recent bookings', 'AuthContext', { restaurantId });

        const since = new Date();
        since.setDate(since.getDate() - 7);
        const sinceStr = since.toISOString().split('T')[0];

        const { data: fresh, error } = await supabase
            .from('bookings')
            .select('*')
            .eq('restaurant_id', restaurantId)
            .gte('booking_date', sinceStr)
            .order('booking_date', { ascending: false });

        if (error) {
            logger.auth.warn('Focus refetch bookings failed', 'AuthContext', { error: error.message });
            return;
        }
        if (!fresh || fresh.length === 0) {
            logger.auth.debug('Focus refetch: no fresh bookings found', 'AuthContext');
            return;
        }

        // Rileggi lo store per avere i dati più aggiornati (potrebbero essere cambiati durante il refetch)
        const currentStore = useRestaurantStore.getState();
        const current = currentStore.data?.bookings ?? [];

        // Merge: i record freschi (ultimi 7gg) sovrascrivono quelli esistenti per id;
        // i record più vecchi (oltre 7gg) vengono preservati dallo store corrente.
        const freshIds = new Set((fresh as BookingRow[]).map(r => r.id));
        const older = current.filter(b => !freshIds.has(b.id));
        const merged = [...(fresh as BookingRow[]).map(mapDbRowToBooking), ...older];
        
        // Aggiorna lo Zustand store
        currentStore.updateBookings(merged);

        // CRITICAL FIX: Sincronizza anche il React state per forzare re-render dei componenti
        // che potrebbero non essere sottoscritti allo store
        setRestaurantData(prev => {
            if (!prev) return prev;
            return { ...prev, bookings: merged };
        });

        logger.auth.info(`Focus refetch: ${fresh.length} fresh + ${older.length} older bookings merged and synced`, 'AuthContext');
    }, []); // Dipendenze vuote: legge tutto da store imperativo

    useAppFocus(handleBookingFocus);

    // Push OneSignal: ri-verifica e ripara lo stato subscription al ritorno in foreground.
    // Prima esisteva solo l'enforce post-login, che su un device già autenticato può non
    // rigirare mai dopo un aggiornamento dell'app (niente nuovo evento SIGNED_IN) — qui è
    // il secondo punto di riparazione. Throttle (~15min) gestito dentro il servizio stesso.
    const handlePushFocus = useCallback(() => {
        const userId = session?.user?.id;
        if (userId) oneSignalService.recheckOnForeground(userId).catch(() => {});
    }, [session?.user?.id]);

    useAppFocus(handlePushFocus);

    // Language sync — single source of truth.
    // Priority: valid local preference > device language (from i18n.ts init).
    // After determining the active language, all layers are synced:
    //   i18n.language ← active language
    //   localStorage   ← active language
    //   SecureStorage   ← active language
    //   DB profile.lang ← active language (non-blocking)
    //   restaurantData  ← active language (local state)
    useEffect(() => {
        if (!restaurantData?.user) return;
        const syncLanguage = async () => {
            let localPref = await storage.get('deskbay_lang');

            // Stale preference detection (iOS Keychain persists across reinstalls).
            // saveLanguagePreference() always writes to BOTH SecureStorage AND localStorage.
            // After a reinstall, localStorage is cleared but Keychain keeps the old value.
            if (localPref && typeof localStorage !== 'undefined' && !localStorage.getItem('deskbay_lang')) {
                await storage.remove('deskbay_lang');
                localPref = null;
            }

            const { default: i18n } = await import('../i18n');

            // Determine active language: valid local pref, or current device language
            const activeLanguage = localPref || i18n.language;

            // If no local preference existed, save the active (device) language
            // as the baseline so all layers stay in sync from now on
            if (!localPref) {
                const { saveLanguagePreference } = await import('../i18n');
                await saveLanguagePreference(activeLanguage);
            }

            // Ensure i18n runtime matches
            if (i18n.language !== activeLanguage) {
                i18n.changeLanguage(activeLanguage);
            }

            // Sync DB profile if out of sync (non-blocking, fire-and-forget)
            if (restaurantData.user.lang !== activeLanguage && restaurantData.user.id) {
                supabaseDb.updateProfile(restaurantData.user.id as string, {
                    firstName: restaurantData.user.firstName,
                    lastName: restaurantData.user.lastName,
                    phone: restaurantData.user.phone,
                    lang: activeLanguage
                }).catch(() => {});
                // Update local state so SettingsEditor and other consumers see the correct value
                setRestaurantData(prev => prev ? {
                    ...prev,
                    user: { ...prev.user, lang: activeLanguage }
                } : prev);
            }
        };
        syncLanguage();
    }, [restaurantData?.user?.lang]);

    // Auth state listener
    useEffect(() => {
        let isMounted = true;

        // event: the Supabase auth event that triggered this change ('INITIAL_SESSION' | 'SIGNED_IN' | null for logout)
        const handleAuthChange = async (newSession: Session | null, event?: string) => {
            if (!isMounted) return;

            const currentSession = sessionRef.current;
            const currentData = restaurantDataRef.current;

            // Il listener si è pronunciato: da qui in poi `session === null` significa
            // davvero "non loggato" e ProtectedRoute può agire di conseguenza.
            setAuthResolved(true);

            // Cambio di identità → invalida ogni caricamento ancora in volo (vedi authEpochRef).
            const nextUserId = newSession?.user?.id ?? null;
            const previousUserId = currentSession?.user?.id ?? null;
            if (nextUserId !== previousUserId) {
                authEpochRef.current += 1;
                logger.auth.info(`Identità cambiata (${previousUserId ?? 'nessuna'} → ${nextUserId ?? 'nessuna'}), epoch ${authEpochRef.current}`, 'handleAuthChange');
            }
            // Epoch di questo specifico cambio auth, letto DOPO l'eventuale incremento.
            const changeEpoch = authEpochRef.current;

            // Same user + data already loaded → skip full reload
            // BUT always update session object (critical for email_confirmed_at after OTP verification)
            //
            // `loadedForUserIdRef` è la terza condizione ed è quella che impedisce di
            // "congelare" uno stato incoerente: se i dati in memoria appartengono a un
            // altro utente, questo ramo veniva preso lo stesso e nessun evento successivo
            // poteva più correggere la situazione fino al riavvio dell'app.
            if (newSession?.user?.id === currentSession?.user?.id
                && currentData
                && loadedForUserIdRef.current === nextUserId) {
                setSession(newSession);
                return;
            }

            setSession(newSession);

            if (newSession) {
                // `loadedForUserIdRef` (id di auth) invece di `currentData.user.id` (id di
                // profilo): sotto impersonificazione i due divergono per costruzione, e
                // confrontare il profilo forzava un ricaricamento ad ogni evento.
                const dataBelongsToUser = !!currentData && loadedForUserIdRef.current === newSession.user.id;

                // Imposta loading solo se cambia utente
                if (currentData && !dataBelongsToUser) {
                    setIsLoading(true);
                }

                if (!dataBelongsToUser) {
                    // Dati del ristorante precedente ancora in memoria: azzerali subito,
                    // altrimenti restano visibili (anche via store Zustand) per tutta la
                    // durata del caricamento del nuovo account.
                    if (currentData) {
                        setRestaurantData(null);
                        setAvailableRestaurants([]);
                        setSelectedRestaurantId(null);
                        useRestaurantStore.getState().reset();
                    }
                    setError(null);
                    try {
                        logger.auth.info(`Loading data for user: ${newSession.user.id}`, 'handleAuthChange');
                        
                        // PERF: Only check deletion_pending on explicit SIGNED_IN (real login), NOT on
                        // INITIAL_SESSION (app startup / token resume). The deletion check is a blocking
                        // RPC call that adds 100-300ms to cold start for a scenario affecting <0.1% of users.
                        // Deletion-pending accounts can still reach dashboard briefly before the
                        // non-blocking background check (in SettingsEditor) catches them.
                        if (event === 'SIGNED_IN') {
                            const { data: deletionCheck } = await supabase.rpc('check_user_deletion_pending', {
                                target_user_id: newSession.user.id
                            });
                            
                            if (deletionCheck?.is_blocked) {
                                logger.auth.warn('User account deletion in progress, blocking access', 'handleAuthChange', { 
                                    userId: newSession.user.id,
                                    deletionRequestedAt: deletionCheck.deletion_requested_at 
                                });
                                
                                // Force logout and redirect to login with deletion message
                                await supabase.auth.signOut({ scope: 'local' });
                                navigate('/login?deletion_pending=true');
                                return;
                            }
                        }
                        
                        await loadAppData(newSession.user.id, newSession.user);

                        // Un altro cambio di identità è avvenuto durante il caricamento:
                        // loadAppData ha già scartato le proprie scritture, qui evitiamo
                        // di marcare i dati come appartenenti a questo utente e di
                        // eseguire redirect/registrazioni push per una sessione superata.
                        if (changeEpoch !== authEpochRef.current) {
                            logger.auth.info('Post-login annullato: identità cambiata durante il caricamento', 'handleAuthChange');
                            return;
                        }

                        loadedForUserIdRef.current = newSession.user.id;

                        // Registra/aggiorna il device token in OneSignal con l'userId corrente.
                        // Chiamato qui (non solo in NotificationContext) per garantire che il token
                        // venga sempre sincronizzato anche dopo reinstall o switch di account.
                        oneSignalService.login(newSession.user.id).catch(() => {});

                        // iOS OAuth post-login redirect: SocialLoginButtons saves the intended
                        // path before opening ASWebAuthenticationSession. We consume it here,
                        // after loadAppData completes, so the app state is fully ready.
                        if (event === 'SIGNED_IN' && isMounted) {
                            const postLoginPath = sessionStorage.getItem('deskbay_post_login_redirect');
                            if (postLoginPath) {
                                sessionStorage.removeItem('deskbay_post_login_redirect');
                                logger.auth.info(`Post-login redirect to: ${postLoginPath}`, 'handleAuthChange');
                                navigate(postLoginPath, { replace: true });
                            }
                        }
                    } catch (e: unknown) {
                        logger.auth.error('Auth change load failed', e, 'handleAuthChange', { userId: newSession?.user?.id });
                        if (isMounted && changeEpoch === authEpochRef.current) {
                            setError(e instanceof Error ? e : new Error(String(e)));
                        }
                    } finally {
                        // Non spegnere lo spinner di un caricamento che nel frattempo
                        // è stato rimpiazzato: lo farà quello corrente.
                        if (isMounted && changeEpoch === authEpochRef.current) setIsLoading(false);
                    }
                }
            } else {
                // Logout: azzera OGNI traccia dell'account precedente.
                // `availableRestaurants`, `selectedRestaurantId` e lo store Zustand
                // restavano popolati e riaffioravano nella sessione successiva
                // (Dashboard fa fallback sullo store quando il context è null).
                setRestaurantData(null);
                setAvailableRestaurants([]);
                setSelectedRestaurantId(null);
                useRestaurantStore.getState().reset();
                loadedForUserIdRef.current = null;
                effectiveUserIdRef.current = null;
                setIsAdmin(false);
                setIsAdminImpersonating(false);
                setIsLoading(false);
                setHasInitialized(true);
                setPageReady(true);
                setError(null);
                oneSignalService.logout().catch(() => {});
            }
        };

        // Safety timeout to prevent infinite loading
        // NOTE: 8 seconds gives enough time for slow connections and native platforms
        // where loadAppData may take 3-5 seconds (async Keychain, heavy menus, etc.)
        //
        // Volutamente NON tocca `authResolved`: sbloccare l'UI è un ripiego per non
        // restare sullo splash, non una prova che l'utente sia sloggato. Prima questo
        // timer poteva scattare mentre la lettura del token dal Keychain era ancora in
        // corso e ProtectedRoute, vedendo session null, mandava su /welcome un utente
        // con sessione valida.
        const safetyTimer = setTimeout(() => {
            if (isLoadingRef.current) {
                setIsLoading(false);
                setHasInitialized(true); // CRITICAL: also unblock splash screen
                setPageReady(true);      // CRITICAL: also unblock splash screen overlay
            }
        }, 8000);

        // Rete di sicurezza per `authResolved`: se INITIAL_SESSION non arrivasse mai
        // (client non inizializzato per env mancanti, listener mai agganciato) l'app
        // resterebbe in attesa per sempre. Oltre questa soglia trattiamo l'assenza di
        // sessione come definitiva e lasciamo che il routing proceda.
        const authResolveTimer = setTimeout(() => {
            setAuthResolved(prev => {
                if (!prev) {
                    logger.auth.warn('Nessun evento auth entro 12s: sessione considerata assente', 'AuthContext');
                }
                return true;
            });
        }, 12000);

        // Single auth listener - handles ALL auth events including initial session
        // This is the Supabase recommended pattern - no separate getSession() needed
        // INITIAL_SESSION fires immediately after client construction with cached session
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            logger.auth.debug(`Auth event: ${event}`, 'onAuthStateChange');

            // Handle password recovery redirect
            if (event === 'PASSWORD_RECOVERY') {
                navigate('/auth/reset-password');
                return;
            }

            // Events that require auth state handling:
            // - INITIAL_SESSION: first session load (replaces getSession())
            // - SIGNED_IN: user logged in
            // - SIGNED_OUT: user logged out
            // TOKEN_REFRESHED is intentionally ignored - no UI action needed
            if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
                handleAuthChange(session, event);
            }

            if (event === 'SIGNED_OUT') {
                // Guard: if SIGNED_OUT happens while loading or right after a resume fetch, 
                // it might be a spurious event from a network error during token refresh.
                // If there's no active session, it's a real logout.
                logger.auth.info('SIGNED_OUT event received', 'onAuthStateChange');
                handleAuthChange(null);
            }

            // Keep session object fresh on token refresh (e.g., after tab focus)
            // No loadAppData needed, just update the session reference
            if (event === 'TOKEN_REFRESHED' && session) {
                setSession(session);
            }

            // MFA challenge verified → session upgraded to aal2, update reference only
            if (event === 'MFA_CHALLENGE_VERIFIED' && session) {
                logger.auth.info('MFA challenge verified, updating session to aal2', 'onAuthStateChange');
                setSession(session);
            }

            // Email change confirmed by user clicking the link in the new email.
            // Sync the updated email from auth.users → profiles table and local state.
            // Only sync when email actually changed — skip no-op events from signInWithPassword re-auth.
            if (event === 'USER_UPDATED' && session) {
                setSession(session);
                const updatedEmail = session.user?.email;
                const userId = session.user?.id;
                const currentData = restaurantDataRef.current;
                const currentEmail = currentData?.user?.email;

                if (updatedEmail && userId && updatedEmail !== currentEmail) {
                    logger.auth.info(`USER_UPDATED: email changed (${currentEmail} → ${updatedEmail}), syncing`, 'onAuthStateChange');
                    try {
                        await supabaseDb.updateProfile(userId, {
                            firstName: currentData?.user?.firstName ?? '',
                            lastName: currentData?.user?.lastName ?? '',
                            email: updatedEmail,
                        });
                        setRestaurantData(prev => prev ? {
                            ...prev,
                            user: { ...prev.user, email: updatedEmail },
                        } : prev);

                        // Record timestamp for 15-day rate limiting
                        await supabase.rpc('record_email_change');
                        logger.auth.info('Email change timestamp recorded', 'onAuthStateChange');

                        // Optional: sync fw360_customers (fire-and-forget, non-blocking)
                        const directoryId = currentData?.directoryId;
                        if (directoryId) {
                            supabase.functions.invoke('sync-fw360-customer', {
                                body: { directoryId, email: updatedEmail }
                            }).then(({ error }) => {
                                if (error) {
                                    logger.auth.warn('fw360_customers sync failed (non-critical)', 'AuthContext', { error: error.message });
                                } else {
                                    logger.auth.debug('fw360_customers synced after email change', 'onAuthStateChange');
                                }
                            }).catch(() => {
                                // Silently ignore — non-critical
                            });
                        }
                    } catch (err: unknown) {
                        const msg = err instanceof Error ? err.message : String(err);
                        logger.auth.warn('Failed to sync email to profiles after USER_UPDATED', 'AuthContext', { error: msg });
                    }
                } else {
                    logger.auth.debug('USER_UPDATED: no email change detected, skipping sync', 'onAuthStateChange');
                }
            }
        });

        return () => {
            isMounted = false;
            clearTimeout(safetyTimer);
            clearTimeout(authResolveTimer);
            subscription.unsubscribe();
        };
    }, [navigate]);

    // Aggiorna SOLO la lista prenotazioni in memoria, su entrambe le sorgenti di
    // verità (React state di AuthContext + Zustand store), come già fa l'handler
    // realtime qui sopra. Nessuna scrittura sul DB: le mutazioni sulla tabella
    // `bookings` sono già state fatte da bookingService.
    const applyLocalBookings = useCallback((updater: (prev: Booking[]) => Booking[]) => {
        const store = useRestaurantStore.getState();
        const next = updater(store.data?.bookings ?? []);
        store.updateBookings(next);
        setRestaurantData(prev => prev ? { ...prev, bookings: next } : prev);
    }, []);

    // Handle data change
    const handleDataChange = async (
        newData: RestaurantData,
        mode: 'full' | 'directory-only' | 'local-only' = 'full'
    ): Promise<boolean> => {
        if (!session?.user?.id || !newData.directoryId) return false;

        // Snapshot the committed state for rollback on error. Captured from the closure (the last
        // committed RestaurantData) rather than as a side-effect inside the setState updater — updaters
        // must stay pure and are not guaranteed to run synchronously before the awaited DB write.
        const previousData: RestaurantData | null = restaurantData;

        try {
            // Apply state optimistically before the DB write so the UI updates immediately.
            // FIX: When mode is NOT 'full', preserve the existing menu in state.
            // Non-menu editors (Profile, Settings, etc.) pass data with menu: []
            // because menu is lazy-loaded only in MenuEditor. Overwriting state
            // with [] would cause the menu to "disappear" when navigating back.
            setRestaurantData(prev => {
                if (!prev) return newData;
                if (mode !== 'full') {
                    return {
                        ...newData,
                        menu: prev.menu.length > 0 ? prev.menu : newData.menu,
                        allergens: prev.allergens?.length > 0 ? prev.allergens : newData.allergens,
                    };
                }
                return newData;
            });

            if (mode !== 'local-only') {
                await supabaseDb.updateRestaurant(newData.directoryId, newData);
            }

            if (mode === 'full') {
                await supabaseDb.saveFullMenu(newData.directoryId, newData.menu);
            }

            return true;
        } catch (e) {
            // Rollback optimistic update on failure.
            if (previousData !== null) setRestaurantData(previousData);
            logger.api.error('Salvataggio dati fallito', e, 'handleDataChange', { restaurantId: newData.directoryId, mode });
            return false;
        }
    };

    // Handle logout
    const handleLogout = async (skipRedirect = false) => {
        // Invalida SUBITO ogni caricamento in volo, prima ancora del signOut: senza
        // questo, una loadAppData partita un istante prima poteva completare dopo il
        // logout e ripopolare lo stato con i dati dell'account appena uscito.
        authEpochRef.current += 1;
        loadedForUserIdRef.current = null;
        effectiveUserIdRef.current = null;

        await supabase.auth.signOut({ scope: 'local' });

        // PKCE: signInWithOAuth salva `<storageKey>-code-verifier`, che signOut non
        // rimuove. Un flow OAuth abbandonato lasciava un verifier orfano capace di far
        // fallire il successivo exchangeCodeForSession.
        try {
            const auth = supabase.auth as unknown as {
                storageKey?: string;
                storage?: { removeItem(key: string): void | Promise<void> };
            };
            if (auth.storageKey && auth.storage) {
                await auth.storage.removeItem(`${auth.storageKey}-code-verifier`);
            }
        } catch {
            // best effort — un verifier orfano non blocca il login con password
        }

        setRestaurantData(null);
        setSession(null);
        // Nessun residuo dell'account uscito: la lista ristoranti alimenta le guardie
        // di routing e lo store Zustand è il fallback di rendering della Dashboard.
        setAvailableRestaurants([]);
        setSelectedRestaurantId(null);
        useRestaurantStore.getState().reset();
        // L'incremento dell'epoch qui sopra fa uscire l'eventuale loadAppData in volo
        // PRIMA del suo finally, quindi lo spinner resterebbe acceso finché non arriva
        // SIGNED_OUT. Lo spegniamo esplicitamente: dopo il logout non c'è nulla da
        // caricare e non dipendiamo da un evento che potrebbe non arrivare.
        setIsLoading(false);
        // [ADMIN] Reset admin status on logout
        setIsAdmin(false);
        setIsAdminImpersonating(false);
        resetAdminRoleCache();
        // Clear Sentry user context
        clearSentryUser();
        // Clear language preference to prevent stale values persisting
        // in Keychain (iOS) across reinstalls
        await storage.remove('deskbay_lang');
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('deskbay_lang');
        }
        if (!skipRedirect) {
            navigate('/welcome');
        }
    };

    const hasPermission = (permission: string): boolean => {
        if (!restaurantData) return false;
        // Owner has full access
        if (restaurantData.currentUserRole === 'owner') return true;
        // Check specific permission
        return !!restaurantData.permissions?.[permission];
    };

    const refreshCurrentRestaurant = useCallback(async () => {
        if (selectedRestaurantId && session?.user?.id) {
            try {
                await loadRestaurantDetail(selectedRestaurantId, session.user.id)
            } catch (err) {
                logger.api.warn('refreshCurrentRestaurant failed', 'AuthContext', { restaurantId: selectedRestaurantId })
            }
        }
    }, [selectedRestaurantId, session?.user?.id])

    const value = {
        session,
        restaurantData,
        availableRestaurants,
        selectedRestaurantId,
        switchRestaurant,
        isLoading,
        hasInitialized,
        authResolved,
        error,
        loadAppData,
        handleDataChange,
        applyLocalBookings,
        handleLogout,
        hasPermission,
        // [ADMIN] Cached admin status
        isAdmin,
        isAdminImpersonating,
        isAdminLoading,
        isPageReady,
        setPageReady,
        refreshCurrentRestaurant
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}


// Hook per usare il context
export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
